import { styles } from './styles';
import { Flex, Box, Pressable, Text } from '../../../../../../../../../mads';
import { Ban, Check } from '../../../../../../../../../mads/icons';
import { useI18n } from '../../../../../../../../../shared/I18n';
import Sentiment from '../../../../../../../../../shared/util/salary/Sentiment';
import AnimatedCheckIcon from '../AnimatedCheckIcon';

import { borderRadius, colors } from '@indeed/ifl-tokens/react-native';
import React from 'react';
import { StyleSheet } from 'react-native';

export interface YesNoButtonProps {
    value: Sentiment;
    isAnimating: boolean;
    disabled: boolean;
    onSubmit: (v: Sentiment) => void;
    accessibilityQuestionText?: string;
}

const getYesResponseButtonStyle = (sentiment: Sentiment, isAnimating: boolean) => {
    let sx = {};
    let textSx = {};
    if (isAnimating && sentiment === Sentiment.POSITIVE) {
        sx = styles.toggleBoxAnimatedYes;
        textSx = styles.toggleBoxTextAnimated;
    } else if (sentiment === Sentiment.POSITIVE) {
        sx = styles.toggleBoxYes;
        textSx = styles.toggleBoxYesText;
    } else {
        sx = styles.toggleBoxUnselected;
        textSx = { color: colors.foregroundActionPrimary };
    }
    return { sx, textSx };
};
const getNoResponseButtonStyle = (sentiment: Sentiment, isAnimating: boolean) => {
    let sx = {};
    let textSx = {};
    if (isAnimating && sentiment === Sentiment.NEGATIVE) {
        sx = styles.toggleBoxAnimatedNo;
        textSx = styles.toggleBoxTextAnimated;
    } else if (sentiment === Sentiment.NEGATIVE) {
        sx = styles.toggleBoxNo;
        textSx = styles.toggleBoxNoText;
    } else {
        sx = styles.toggleBoxUnselected;
        textSx = { color: colors.foregroundActionPrimary };
    }
    return { sx, textSx };
};

export const YesNoButtons: React.FunctionComponent<YesNoButtonProps> = ({
    isAnimating,
    value,
    disabled,
    onSubmit,
    accessibilityQuestionText,
}) => {
    const { gettext } = useI18n();
    const { sx: yesSx, textSx: yesTextSx } = getYesResponseButtonStyle(value, isAnimating);
    const { sx: noSx, textSx: noTextSx } = getNoResponseButtonStyle(value, isAnimating);

    return (
        <Flex sx={{ flexDirection: 'row' }}>
            <Pressable
                disabled={disabled}
                onPress={() => onSubmit(Sentiment.POSITIVE)}
                sx={[buttonStyles.button, buttonStyles.leadingButton, yesSx]}
                accessibilityRole="button"
                accessibilityState={{ selected: value === Sentiment.POSITIVE }}
                accessibilityHint={
                    accessibilityQuestionText
                        ? gettext('Answer to {0}', [accessibilityQuestionText])
                        : undefined
                }
            >
                {isAnimating && value === Sentiment.POSITIVE ? (
                    <AnimatedCheckIcon />
                ) : (
                    <Box sx={{ flexDirection: 'row', alignItems: 'center' }}>
                        {value === Sentiment.POSITIVE && (
                            <Check color={colors.foregroundStatusSuccess} />
                        )}
                        <Text level={3} sx={[yesTextSx, buttonStyles.buttonText]}>
                            {gettext('Yes')}
                        </Text>
                    </Box>
                )}
            </Pressable>
            <Pressable
                disabled={disabled}
                onPress={() => onSubmit(Sentiment.NEGATIVE)}
                sx={[buttonStyles.button, buttonStyles.trailingButton, noSx]}
                accessibilityRole="button"
                accessibilityState={{ selected: value === Sentiment.NEGATIVE }}
                accessibilityHint={
                    accessibilityQuestionText
                        ? gettext('Answer to {0}', [accessibilityQuestionText])
                        : undefined
                }
            >
                {isAnimating && value === Sentiment.NEGATIVE ? (
                    <AnimatedCheckIcon />
                ) : (
                    <Box sx={{ flexDirection: 'row', alignItems: 'center' }}>
                        {value === Sentiment.NEGATIVE && (
                            <Ban color={colors.foregroundStatusWarning} />
                        )}
                        <Text level={3} sx={[noTextSx, buttonStyles.buttonText]}>
                            {gettext('No')}
                        </Text>
                    </Box>
                )}
            </Pressable>
        </Flex>
    );
};

const buttonStyles = StyleSheet.create({
    button: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    leadingButton: {
        borderTopLeftRadius: borderRadius.md,
        borderBottomLeftRadius: borderRadius.md,
    },
    trailingButton: {
        borderTopRightRadius: borderRadius.md,
        borderBottomRightRadius: borderRadius.md,
    },
    buttonText: {
        fontWeight: 'bold',
    },
});
